// src/sectors/pilates/settings/tabs/plans/components/PlanListSection.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

function fmtTry(n) {
  return `₺${Number(n || 0).toLocaleString('tr-TR')}`;
}

function itemsSummary(items) {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) return 'İçerik yok';
  return arr
    .map((it) => {
      const name = String(it?.activityName || 'Faaliyet');
      const s = Number(it?.sessions || 0);
      const m = Number(it?.makeUp || 0);
      const gift = it?.gift ? '🎁' : '';
      return `${name}: ${s}(+${m})${gift}`;
    })
    .join(' • ');
}

export function PlanListSection({
  plansLoading,
  plans = [],
  onOpenCreateModal,
  onEditPlan,
  onToggleActive, // (id, nextActive) => void
}) {
  const theme = useTheme();
  const list = Array.isArray(plans) ? plans : [];

  return (
    <MDBox sx={{ display: 'grid', gap: 1.1 }}>
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <MDTypography variant="button" fontWeight="bold">
          Paketler
        </MDTypography>

        <MDButton
          variant="text"
          color="info"
          sx={{ borderRadius: '12px', px: 1.2, minHeight: 34 }}
          onClick={onOpenCreateModal}
          disabled={plansLoading}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>add</Icon>
          Yeni Paket
        </MDButton>
      </MDBox>

      <Divider />

      {plansLoading ? (
        <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
          Yükleniyor...
        </MDTypography>
      ) : list.length === 0 ? (
        <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
          Henüz paket yok.
        </MDTypography>
      ) : (
        <MDBox sx={{ display: 'grid', gap: 1 }}>
          {list.map((p) => {
            const price = p?.priceTry != null ? fmtTry(p.priceTry) : '₺0';
            const cap = p?.classCapacity ?? 1;
            const detail = itemsSummary(p?.items);
            const isActive = p?.active !== false;

            return (
              <MDBox
                key={String(p.id)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '16px',
                  p: 1.1,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <MDBox sx={{ minWidth: 0 }}>
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ mb: 0.25 }}>
                    <MDTypography
                      fontWeight="bold"
                      sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {p?.name || '—'}
                    </MDTypography>

                    {!isActive ? <Chip size="small" label="Pasif" variant="outlined" /> : null}
                  </MDBox>

                  <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                    {price} • Kapasite: {cap}
                  </MDTypography>

                  <Tooltip title={detail} placement="top" arrow>
                    <MDTypography
                      variant="caption"
                      sx={{
                        mt: 0.3,
                        color: 'text.secondary',
                        opacity: 0.9,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '100%',
                      }}
                    >
                      {detail}
                    </MDTypography>
                  </Tooltip>
                </MDBox>

                <MDBox display="flex" justifyContent="flex-end" gap={0.5}>
                  <Tooltip title="Düzenle">
                    <IconButton
                      onClick={() => onEditPlan?.(p.id)}
                      sx={{ borderRadius: '12px' }}
                      size="small"
                    >
                      <Icon sx={{ fontSize: 18, color: theme.palette.text.primary }}>edit</Icon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={isActive ? 'Pasife al' : 'Aktifleştir'}>
                    <IconButton
                      onClick={() => onToggleActive?.(p.id, !isActive)}
                      sx={{ borderRadius: '12px' }}
                      size="small"
                    >
                      <Icon
                        sx={{
                          fontSize: 18,
                          color: isActive ? theme.palette.error.main : theme.palette.success.main,
                        }}
                      >
                        {isActive ? 'block' : 'check_circle'}
                      </Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </MDBox>
  );
}
